import { Controller } from "@hotwired/stimulus";

/**
 * Shows a circular client-side preview of a selected avatar image before the
 * form is submitted, and (where the targets are present) validates the file
 * size up front so an oversized upload is caught in the dialog rather than
 * bouncing off the server. Wired by the avatar form field partial and the
 * admin avatar widget.
 *
 * The `error` and `submit` targets are optional: the attendee profile form
 * wires only `input`/`preview`, so the validation/gating is skipped there.
 */
export class AvatarFieldController extends Controller {
  static targets = ["input", "preview", "error", "submit"];
  // Mirrors the server-side 5MB limit (Attendee#acceptable_avatar).
  static values = { maxBytes: { type: Number, default: 5 * 1024 * 1024 } };

  connect() {
    // Nothing chosen yet, so a submit gated on this control starts disabled.
    this.setSubmitEnabled(false);
  }

  preview() {
    const file = this.inputTarget.files && this.inputTarget.files[0];
    if (!file) {
      this.clearError();
      this.setSubmitEnabled(false);
      return;
    }

    if (file.size > this.maxBytesValue) {
      this.showError("That image is over 5MB. Please choose a smaller file.");
      this.setSubmitEnabled(false);
      return;
    }

    this.clearError();
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
    this.objectUrl = URL.createObjectURL(file);
    this.previewTarget.src = this.objectUrl;
    this.previewTarget.hidden = false;
    this.setSubmitEnabled(true);
  }

  setSubmitEnabled(enabled) {
    if (this.hasSubmitTarget) this.submitTarget.disabled = !enabled;
  }

  showError(message) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = message;
    this.errorTarget.hidden = false;
  }

  clearError() {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = "";
    this.errorTarget.hidden = true;
  }

  disconnect() {
    if (this.objectUrl) URL.revokeObjectURL(this.objectUrl);
  }
}
